import React, { useState } from 'react';
import {
  signIn,
  confirmSignIn,
  rememberDevice,
  resetPassword,
  confirmResetPassword,
  signOut,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/data';
import { useAuth } from '../../contexts/AuthContext';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';

// apiKey — this form runs before the user is signed in, same as the public
// registration wizard's client.
const client = generateClient({ authMode: 'apiKey' });

interface LoginFormProps {
  onSuccess?: () => void;
  onSwitchToSignUp?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onSwitchToSignUp }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<
    | 'signin'
    | 'newpassword'
    | 'mfa'
    | 'mfaselect'
    | 'resetpassword'
    | 'confirmresetpassword'
    | 'choose'
  >('signin');
  const [mfaTypes, setMfaTypes] = useState<string[]>([]);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [trustThisDevice, setTrustThisDevice] = useState(false); // Req 1.1 — unticked default
  const { refreshAuth } = useAuth();

  // Called at every successful sign-in point. Admins are offered a choice of
  // destination (admin dashboard vs member area); regular members proceed
  // straight through. We deliberately do NOT refreshAuth() before showing the
  // chooser: refreshing flips the surrounding ProtectedRoute to its
  // authenticated child and unmounts this form before the chooser can render.
  const completeSignIn = async () => {
    try {
      const session = await fetchAuthSession();
      const groups = (session.tokens?.accessToken?.payload['cognito:groups'] as string[]) || [];
      if (groups.includes('Admins')) {
        setStep('choose');
        return;
      }
    } catch {
      /* fall through to normal member sign-in */
    }
    await refreshAuth();
    onSuccess?.();
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Clear any stale Cognito session before attempting sign in
      try {
        await signOut();
      } catch (_) {
        /* ignore */
      }

      const result = await signIn({
        username: email,
        password: password,
      });

      if (result.isSignedIn) {
        await completeSignIn();
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setStep('newpassword');
      } else if (
        result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
        result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
        result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
      ) {
        setStep('mfa');
      } else if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
        // Admin has more than one factor (e.g. TOTP + SMS) — let them choose.
        setMfaTypes((result.nextStep.allowedMFATypes as string[]) ?? []);
        setStep('mfaselect');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await confirmSignIn({
        challengeResponse: newPassword,
      });

      if (result.isSignedIn) {
        await completeSignIn();
      } else if (
        result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
        result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
        result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
      ) {
        setStep('mfa');
      } else if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
        setMfaTypes((result.nextStep.allowedMFATypes as string[]) ?? []);
        setStep('mfaselect');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to set new password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMFA = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await confirmSignIn({
        challengeResponse: mfaCode,
      });

      if (result.isSignedIn) {
        if (trustThisDevice) {
          try {
            await rememberDevice(); // Req 1.2
          } catch {
            // Non-fatal — sign-in still completes (Req 1.5)
            setError('You are signed in, but this device could not be remembered.');
          }
        }
        await completeSignIn();
      }
    } catch (err: any) {
      setError(err.message || 'Invalid MFA code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSelect = async (mfaType: string) => {
    setIsLoading(true);
    setError('');
    try {
      const result = await confirmSignIn({ challengeResponse: mfaType });
      if (
        result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
        result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE'
      ) {
        setStep('mfa');
      } else if (result.isSignedIn) {
        await completeSignIn();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to select an MFA method');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Catches the one case Cognito's self-service ForgotPassword flow
      // cannot handle at all: FORCE_CHANGE_PASSWORD (a never-used or
      // expired temporary password). Cognito does not throw an error for
      // this — resetPassword() below would succeed and silently send no
      // code, leaving the member stuck on a "check your email" screen
      // forever. See check-password-reset-eligibility/rules.ts.
      //
      // Deliberately fails open in its own try/catch: this check is an
      // additive UX improvement, never a gate that can itself block a
      // reset that would otherwise have worked if it errors or times out.
      try {
        const eligibilityResult = await (client.queries as any).checkPasswordResetEligibility({
          email: resetEmail,
        });
        const eligibility = parseLambdaResponse<{ eligibility: string }>(eligibilityResult?.data);

        if (eligibility?.eligibility === 'needs-admin-reset') {
          setError(
            "We can't send a reset code to this account automatically — your temporary password has expired. Please contact the club at fishing@tumbyfc.com and we'll issue you a new one."
          );
          setIsLoading(false);
          return;
        }
      } catch {
        /* fail open — fall through to the normal self-service flow below */
      }

      await resetPassword({ username: resetEmail });
      setStep('confirmresetpassword');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resetNewPassword !== resetConfirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await confirmResetPassword({
        username: resetEmail,
        confirmationCode: resetCode,
        newPassword: resetNewPassword,
      });
      setError('Password reset successful. Please sign in with your new password.');
      setResetCode('');
      setResetNewPassword('');
      setResetConfirmPassword('');
      setStep('signin');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'newpassword') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Set New Password
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Please set a new password for your account
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleNewPassword}>
            <div className="space-y-4">
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <input
                  id="new-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue focus:z-10 sm:text-sm"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor="confirm-password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirm Password
                </label>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue focus:z-10 sm:text-sm"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>

            {error && <div className="text-red-600 text-sm text-center">{error}</div>}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-fishing-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue disabled:opacity-50"
              >
                {isLoading ? 'Setting Password...' : 'Set New Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (step === 'mfaselect') {
    const label = (t: string) =>
      t === 'TOTP' ? 'Authenticator app' : t === 'EMAIL' ? 'Email code' : t;
    // SMS is disabled pending SNS production access — hide it from the chooser.
    const availableTypes = mfaTypes.filter((t) => t !== 'SMS');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Choose a verification method
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Select how you&apos;d like to receive your code
            </p>
          </div>
          <div className="space-y-3">
            {availableTypes.map((t) => (
              <button
                key={t}
                type="button"
                disabled={isLoading}
                onClick={() => handleMfaSelect(t)}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-fishing-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue disabled:opacity-50"
              >
                {label(t)}
              </button>
            ))}
          </div>
          {error && <div className="text-red-600 text-sm text-center">{error}</div>}
        </div>
      </div>
    );
  }

  if (step === 'mfa') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Enter MFA Code
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Please enter the code from your authenticator app, text message, or email
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleMFA}>
            <div>
              <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700">
                MFA Code
              </label>
              <input
                id="mfa-code"
                name="mfa-code"
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue focus:z-10 sm:text-sm"
                placeholder="Enter 6-digit code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                maxLength={6}
              />
            </div>

            {error && <div className="text-red-600 text-sm text-center">{error}</div>}

            <div className="flex items-start gap-3">
              <input
                id="trust-device"
                type="checkbox"
                checked={trustThisDevice}
                onChange={(e) => setTrustThisDevice(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-fishing-blue focus:ring-fishing-blue"
              />
              <label htmlFor="trust-device" className="text-sm text-gray-600">
                Remember this device for 30 days (skip the code next time you sign in)
              </label>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-fishing-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue disabled:opacity-50"
              >
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (step === 'resetpassword') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Reset Password
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Enter your email to receive a password reset code
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleResetPassword}>
            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                id="reset-email"
                name="reset-email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue focus:z-10 sm:text-sm"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
            </div>

            {error && <div className="text-red-600 text-sm text-center">{error}</div>}

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => {
                  setStep('signin');
                  setError('');
                }}
                className="flex-1 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue"
              >
                Back to Sign In
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-fishing-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue disabled:opacity-50"
              >
                {isLoading ? 'Sending...' : 'Send Reset Code'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (step === 'confirmresetpassword') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Set New Password
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              If an account exists with that email, you will receive a code within a few minutes.
              Check your spam folder.
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleConfirmResetPassword}>
            <div className="space-y-4">
              <div>
                <label htmlFor="reset-code" className="block text-sm font-medium text-gray-700">
                  Verification Code
                </label>
                <input
                  id="reset-code"
                  name="reset-code"
                  type="text"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue focus:z-10 sm:text-sm"
                  placeholder="Enter 6-digit code"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  maxLength={6}
                />
              </div>
              <div>
                <label
                  htmlFor="reset-new-password"
                  className="block text-sm font-medium text-gray-700"
                >
                  New Password
                </label>
                <input
                  id="reset-new-password"
                  name="reset-new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue focus:z-10 sm:text-sm"
                  placeholder="Enter new password"
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor="reset-confirm-password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirm New Password
                </label>
                <input
                  id="reset-confirm-password"
                  name="reset-confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue focus:z-10 sm:text-sm"
                  placeholder="Confirm new password"
                  value={resetConfirmPassword}
                  onChange={(e) => setResetConfirmPassword(e.target.value)}
                />
              </div>
            </div>

            {error && <div className="text-red-600 text-sm text-center">{error}</div>}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-fishing-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue disabled:opacity-50"
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setStep('resetpassword');
                  setError('');
                }}
                className="text-sm text-fishing-blue hover:text-blue-700"
              >
                Didn't receive a code? Try again
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (step === 'choose') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto h-24 w-24 flex items-center justify-center">
              <img
                src="/tumbulgum-logo.png"
                alt="Tumbulgum Fishing Club Logo"
                className="h-24 w-auto"
              />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Welcome back</h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              You&apos;re signed in as an admin. Where would you like to go?
            </p>
          </div>
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => window.location.assign('/admin')}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-base font-semibold rounded-md text-white bg-fishing-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue"
            >
              Admin Dashboard
            </button>
            <button
              type="button"
              onClick={() => window.location.assign('/dashboard')}
              className="group relative w-full flex justify-center py-3 px-4 border border-gray-300 text-base font-semibold rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue"
            >
              Member Area
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-24 w-24 flex items-center justify-center">
            <img
              src="/tumbulgum-logo.png"
              alt="Tumbulgum Fishing Club Logo"
              className="h-24 w-auto"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {window.location.pathname.startsWith('/admin') ? 'Admin Login' : 'Login'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {window.location.pathname.startsWith('/admin')
              ? 'Sign in to access the admin dashboard'
              : 'Sign in to access your membership details'}
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSignIn}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue focus:z-10 sm:text-sm"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const form = e.currentTarget.closest('form');
                    form?.requestSubmit();
                  }
                }}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue focus:z-10 sm:text-sm"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const form = e.currentTarget.closest('form');
                    form?.requestSubmit();
                  }
                }}
              />
            </div>
          </div>

          {error && (
            <div
              className={`text-sm text-center ${error.includes('successful') ? 'text-green-600' : 'text-red-600'}`}
            >
              {error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-fishing-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue disabled:opacity-50"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setStep('resetpassword')}
              className="text-sm text-fishing-blue hover:text-blue-700"
            >
              Forgot your password?
            </button>
          </div>

          {onSwitchToSignUp && !window.location.pathname.startsWith('/admin') && (
            <div className="text-center">
              <button
                type="button"
                onClick={onSwitchToSignUp}
                className="text-sm text-fishing-blue hover:text-blue-700"
              >
                Don't have an account? Sign up
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
